import { Capacitor } from '@capacitor/core';

// Detect when iOS Safari is being viewed as a regular browser tab vs.
// a PWA installed to the Home Screen.
//
// iOS web push has a hard constraint: pushes only deliver to PWAs that
// were installed to Home Screen via Share → Add to Home Screen, then
// opened from that icon. Safari tabs (even ones with notification
// permission granted) silently never receive push.
//
// This is the #1 reason iOS users on The Standard don't get push —
// they tap "Allow notifications" in a Safari tab, see a success toast,
// and then nothing ever lands. We surface this state so the UI can
// stop pretending push is enabled and tell them the real fix.

export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  // iPadOS reports as macOS — sniff for touch + iPad-specific ratio.
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (ua.includes('Mac') && 'ontouchend' in document) return true;
  return false;
}

export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS Safari sets navigator.standalone when launched from Home Screen.
  // Modern browsers (Chrome, Edge, etc.) report via display-mode: standalone.
  const navStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  if (navStandalone === true) return true;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  return false;
}

export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** True when iOS users are in a Safari tab — push will not deliver here. */
export function needsIOSHomeScreenInstall(): boolean {
  return !isNativeApp() && isIOSDevice() && !isStandalonePWA();
}
